/**
 * SectionManager.jsx - Gestión de Secciones para Cursos Grupales
 * 
 * Permite a teachers/admins:
 * - Crear nuevas secciones de cursos
 * - Listar secciones del instructor
 * - Editar horarios y metadata
 * - Ver grupos asociados a cada sección
 * - Archivar secciones
 * 
 * Solo accesible para usuarios con rol 'teacher' o 'admin'
 * 
 * @component
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  Calendar, 
  Clock, 
  Users, 
  Plus, 
  Edit, 
  Trash2, 
  Save,
  X,
  Loader,
  AlertCircle,
  CheckCircle,
  MapPin,
  BookOpen,
  ChevronRight
} from 'lucide-react';
import apiClient from '../services/apiClient';

const SectionManager = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Estados principales
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Estados del formulario
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingSectionId, setEditingSectionId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    sectionId: '',
    courseId: '',
    schedule: '',
    semester: '',
    maxStudents: 30,
    location: ''
  });
  
  // Estados de UI
  const [groupCourses, setGroupCourses] = useState([]);
  
  // Cargar secciones y cursos grupales al montar
  useEffect(() => {
    fetchSections();
    fetchGroupCourses();
  }, []);
  
  // Auto-limpiar mensajes después de 5 segundos
  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess(null);
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  /**
   * Obtener cursos grupales disponibles para el dropdown
   */
  const fetchGroupCourses = async () => {
    try {
      const response = await apiClient.get('/admin/group-courses');
      const courses = response.data.courses || response.data.data || [];
      setGroupCourses(courses);
    } catch (err) {
      console.error('Error al cargar cursos grupales:', err);
    }
  };

  /**
   * Obtener todas las secciones del instructor
   */
  const fetchSections = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      
      const response = await apiClient.get('/sections');
      
      if (response.data.success) {
        setSections(response.data.sections || []);
      }
    } catch (err) {
      console.error('Error al cargar secciones:', err);
      if (!silent) setError(err.response?.data?.error || 'Error al cargar las secciones');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  /**
   * Crear nueva sección
   */
  const handleCreateSection = async (e) => {
    e.preventDefault();
    if (submitting) return;
    
    try {
      setSubmitting(true);
      setError(null);
      setSuccess(null);
      
      // Validaciones básicas
      if (!formData.sectionId || !formData.courseId || !formData.schedule || !formData.semester) {
        setError('Todos los campos obligatorios deben estar completos');
        return;
      }
      
      const payload = {
        sectionId: formData.sectionId,
        courseId: formData.courseId,
        instructorId: user._id || user.id,
        schedule: formData.schedule,
        semester: formData.semester,
        metadata: {
          maxStudents: parseInt(formData.maxStudents) || 30,
          location: formData.location || 'Virtual'
        }
      };
      
      const response = await apiClient.post('/sections', payload);
      
      if (response.data.success) {
        setSuccess('Sección creada exitosamente');
        resetForm();
        fetchSections(true); // Recargar lista en modo silencioso (no pisa el mensaje de éxito)
      }
    } catch (err) {
      console.error('Error al crear sección:', err);
      setError(err.response?.data?.error || 'Error al crear la sección');
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Actualizar sección existente
   */
  const handleUpdateSection = async (sectionId) => {
    try {
      setError(null);
      setSuccess(null);
      
      const payload = {
        schedule: formData.schedule,
        metadata: {
          maxStudents: parseInt(formData.maxStudents) || 30,
          location: formData.location || 'Virtual'
        }
      };
      
      const response = await apiClient.put(`/sections/${sectionId}`, payload);
      
      if (response.data.success) {
        setSuccess('Sección actualizada exitosamente');
        setEditingSectionId(null);
        resetForm();
        fetchSections(true);
      }
    } catch (err) {
      console.error('Error al actualizar sección:', err);
      setError(err.response?.data?.error || 'Error al actualizar la sección');
    }
  };

  /**
   * Archivar sección
   */
  const handleArchiveSection = async (sectionId) => {
    if (!window.confirm('¿Estás seguro de archivar esta sección?')) {
      return;
    }
    
    try {
      setError(null);
      
      await apiClient.delete(`/sections/${sectionId}`);
      
      setSuccess('Sección archivada exitosamente');
      fetchSections(true);
    } catch (err) {
      console.error('Error al archivar sección:', err);
      setError(err.response?.data?.error || 'Error al archivar la sección');
    }
  };

  /**
   * Resetear formulario
   */
  const resetForm = () => {
    setFormData({
      sectionId: '',
      courseId: '',
      schedule: '',
      semester: '',
      maxStudents: 30,
      location: ''
    });
    setShowCreateForm(false);
    setEditingSectionId(null);
  };

  /**
   * Iniciar edición de sección
   */
  const startEdit = (section) => {
    setFormData({
      sectionId: section.sectionId,
      courseId: section.courseId,
      schedule: section.schedule,
      semester: section.semester,
      maxStudents: section.metadata?.maxStudents || 30,
      location: section.metadata?.location || ''
    });
    setEditingSectionId(section.sectionId);
    setShowCreateForm(false);
  };

  // Si no es teacher o admin, no mostrar
  if (user?.role !== 'teacher' && user?.role !== 'admin') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-800 mb-2">Acceso Denegado</h2>
          <p className="text-slate-600">
            Solo teachers y administradores pueden acceder a esta sección.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-slate-800 flex items-center gap-3">
                <Calendar className="w-8 h-8 text-blue-600" />
                Gestión de Secciones
              </h1>
              <p className="text-slate-600 mt-2">
                Administra horarios y secciones de tus cursos grupales
              </p>
            </div>
            
            <button
              onClick={() => {
                const next = !showCreateForm;
                setShowCreateForm(next);
                setEditingSectionId(null);
                if (next) {
                  // Solo limpiar campos del formulario, sin cambiar showCreateForm
                  setFormData({
                    sectionId: '',
                    courseId: '',
                    schedule: '',
                    semester: '',
                    maxStudents: 30,
                    location: ''
                  });
                }
              }}
              className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-all shadow-lg hover:shadow-xl"
            >
              {showCreateForm ? (
                <>
                  <X className="w-5 h-5" />
                  Cancelar
                </>
              ) : (
                <>
                  <Plus className="w-5 h-5" />
                  Nueva Sección
                </>
              )}
            </button>
          </div>
        </div>

        {/* Mensajes de éxito/error */}
        {success && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6 flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <p className="text-green-800">{success}</p>
          </div>
        )}
        
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Formulario de creación */}
        {showCreateForm && (
          <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6">
            <h2 className="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-2">
              <Plus className="w-6 h-6 text-blue-600" />
              Crear Nueva Sección
            </h2>
            
            <form onSubmit={handleCreateSection} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Section ID */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    ID de Sección *
                  </label>
                  <input
                    type="text"
                    placeholder="lean-startup-lunes-7pm"
                    value={formData.sectionId}
                    onChange={(e) => setFormData({...formData, sectionId: e.target.value})}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                  <p className="text-xs text-slate-500 mt-1">Formato: solo letras minúsculas, números y guiones</p>
                </div>

                {/* Course ID - dropdown */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Curso Grupal *
                  </label>
                  <select
                    value={formData.courseId}
                    onChange={(e) => setFormData({...formData, courseId: e.target.value})}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                    required
                  >
                    <option value="">Selecciona un curso...</option>
                    {groupCourses.map(gc => (
                      <option key={gc.courseId} value={gc.courseId}>
                        {gc.title || gc.courseId} ({gc.courseId})
                      </option>
                    ))}
                  </select>
                  {groupCourses.length === 0 && (
                    <p className="text-xs text-amber-600 mt-1">No hay cursos grupales disponibles. Crea uno en el panel de admin.</p>
                  )}
                </div>

                {/* Schedule */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Horario *
                  </label>
                  <input
                    type="text"
                    placeholder="Lunes 7:00-9:00 PM"
                    value={formData.schedule}
                    onChange={(e) => setFormData({...formData, schedule: e.target.value})}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                {/* Semester */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Semestre *
                  </label>
                  <input
                    type="text"
                    placeholder="2026-1"
                    value={formData.semester}
                    onChange={(e) => setFormData({...formData, semester: e.target.value})}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                {/* Max Students */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Estudiantes Máximo
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    value={formData.maxStudents}
                    onChange={(e) => setFormData({...formData, maxStudents: e.target.value})}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Location */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Ubicación
                  </label>
                  <input
                    type="text"
                    placeholder="Aula Virtual 1"
                    value={formData.location}
                    onChange={(e) => setFormData({...formData, location: e.target.value})}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed text-white rounded-xl transition-all"
                >
                  {submitting ? (
                    <Loader className="w-5 h-5 animate-spin" />
                  ) : (
                    <Save className="w-5 h-5" />
                  )}
                  {submitting ? 'Creando...' : 'Crear Sección'}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  disabled={submitting}
                  className="px-6 py-3 bg-slate-200 hover:bg-slate-300 disabled:opacity-60 text-slate-700 rounded-xl transition-all"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Lista de secciones */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8">
          <h2 className="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-2">
            <BookOpen className="w-6 h-6 text-blue-600" />
            Mis Secciones ({sections.length})
          </h2>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader className="w-8 h-8 text-blue-600 animate-spin" />
            </div>
          ) : sections.length === 0 ? (
            <div className="text-center py-12">
              <Calendar className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600 text-lg">
                No tienes secciones creadas aún
              </p>
              <p className="text-slate-500 mt-2">
                Haz clic en "Nueva Sección" para comenzar
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {sections.map((section) => (
                <div
                  key={section._id}
                  className="border border-slate-200 rounded-xl overflow-hidden hover:shadow-md hover:border-blue-300 transition-all"
                >
                  {editingSectionId === section.sectionId ? (
                    /* Formulario de edición inline */
                    <div className="p-4 bg-white">
                      <h3 className="text-base font-semibold text-slate-700 mb-3">
                        Editando: {section.sectionId}
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">
                            Horario
                          </label>
                          <input
                            type="text"
                            value={formData.schedule}
                            onChange={(e) => setFormData({...formData, schedule: e.target.value})}
                            className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">
                            Ubicación
                          </label>
                          <input
                            type="text"
                            value={formData.location}
                            onChange={(e) => setFormData({...formData, location: e.target.value})}
                            className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">
                            Estudiantes máximo
                          </label>
                          <input
                            type="number"
                            value={formData.maxStudents}
                            onChange={(e) => setFormData({...formData, maxStudents: e.target.value})}
                            className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                      <div className="flex gap-3 mt-4">
                        <button
                          onClick={() => handleUpdateSection(section.sectionId)}
                          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm"
                        >
                          <Save className="w-4 h-4" />
                          Guardar Cambios
                        </button>
                        <button
                          onClick={() => { setEditingSectionId(null); }}
                          className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg text-sm"
                        >
                          Cancelar
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* Vista normal - tarjeta clickeable */
                    <div
                      onClick={() => navigate(`/teaching/sections/${section.sectionId}/groups`)}
                      className="p-4 bg-slate-50 cursor-pointer group"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="text-lg font-semibold text-slate-800 group-hover:text-blue-700 transition-colors">
                              {section.sectionId}
                            </h3>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              section.status === 'active'
                                ? 'bg-green-100 text-green-700'
                                : 'bg-slate-100 text-slate-600'
                            }`}>
                              {section.status === 'active' ? 'Activa' : 'Archivada'}
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-4 mt-2 text-sm text-slate-600">
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {section.schedule}
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {section.semester}
                            </span>
                            <span className="flex items-center gap-1">
                              <Users className="w-4 h-4" />
                              {section.groupsCount || 0} grupos
                            </span>
                            {section.metadata?.location && (
                              <span className="flex items-center gap-1">
                                <MapPin className="w-4 h-4" />
                                {section.metadata.location}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-blue-600 mt-2 group-hover:underline">
                            Ver y gestionar grupos →
                          </p>
                        </div>

                        <div className="flex items-center gap-1 ml-4">
                          <button
                            onClick={(e) => { e.stopPropagation(); startEdit(section); }}
                            title="Editar sección"
                            className="p-2 hover:bg-blue-100 text-slate-500 hover:text-blue-700 rounded-lg transition-all"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleArchiveSection(section.sectionId); }}
                            title="Archivar sección"
                            className="p-2 hover:bg-red-100 text-slate-500 hover:text-red-600 rounded-lg transition-all"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                          <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-blue-600 transition-colors ml-1" />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SectionManager;
